import { ChangeDetectionStrategy, Component, OnInit, computed, inject, input, output, signal } from '@angular/core';
import { CurrencyPipe, DatePipe } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  TeeTimeActionPayload,
  TeeTimeJoinWaitlistAction,
  WaitlistFormData,
  WaitlistFormSelection,
  WaitlistPaymentType,
} from '../../../model/chat.model';
import { SharedService } from '../../../shared/services/shared.service';

@Component({
  selector: 'app-waitlist-form',
  imports: [ReactiveFormsModule, CurrencyPipe, DatePipe],
  templateUrl: './waitlist-form.component.html',
  styleUrl: './waitlist-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WaitlistFormComponent implements OnInit {
  private readonly _fb = inject(FormBuilder);
  private readonly _shared = inject(SharedService);

  readonly data = input.required<WaitlistFormData>();
  readonly embedded = input<boolean>(false);
  /** When false (stale chat message) the form is read-only. */
  readonly interactive = input<boolean>(true);
  readonly action = output<TeeTimeActionPayload>();

  protected readonly form: FormGroup = this._fb.group({
    phone: ['', [Validators.required, Validators.pattern(/^[+0-9()\-\s]{6,}$/)]],
    paymentType: ['' as WaitlistPaymentType | '', Validators.required],
    payOnsiteChecked: [true],
  });

  protected readonly submitted = signal(false);

  protected readonly currentUser = toSignal(this._shared.user$, { initialValue: undefined });

  protected readonly selection = computed<WaitlistFormSelection>(() => this.data().selection);
  protected readonly costSummary = computed(() => this.data().costSummary);
  protected readonly termsAndConditions = computed(() => this.data().termsAndConditions);

  protected readonly locationDisplay = computed(() => {
    const sel = this.selection();
    return sel.courseLocation || sel.place || '';
  });

  protected readonly currency = computed(() => this.costSummary()?.currency ?? 'USD');

  /** Unicode regional-indicator flag from an ISO 3166-1 alpha-2 country code (e.g. "US" → "🇺🇸"). */
  protected readonly countryFlag = computed(() => {
    const code = (this.selection().countryCode ?? '').trim().toUpperCase();
    if (code.length !== 2) return '';
    const base = 0x1f1e6;
    const a = 'A'.charCodeAt(0);
    return String.fromCodePoint(base + (code.charCodeAt(0) - a), base + (code.charCodeAt(1) - a));
  });

  ngOnInit(): void {
    const sel = this.selection();
    const payOnsite = this.costSummary()?.payOnsiteOption;
    this.form.patchValue({
      phone: sel.phone ?? '',
      paymentType: sel.defaultPaymentType ?? sel.paymentTypeOptions?.[0] ?? '',
      payOnsiteChecked: payOnsite?.checked ?? true,
    });
    // When the BE marks the option as "not available" (i.e. it's the only choice in v1),
    // lock the checkbox so the user can't uncheck it. The control is still emitted on submit.
    if (payOnsite && payOnsite.available === false) {
      this.form.get('payOnsiteChecked')?.disable({ emitEvent: false });
    }
    if (!this.interactive()) {
      this.form.disable({ emitEvent: false });
    }
  }

  protected submit(): void {
    if (this.submitted() || !this.interactive()) return;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const sel = this.selection();
    const raw = this.form.getRawValue() as { phone: string; paymentType: WaitlistPaymentType };

    const payload: TeeTimeJoinWaitlistAction = {
      type: 'join_waitlist',
      courseName: sel.courseName,
      locationId: sel.locationId,
      ...(sel.courseLocation ? { courseLocation: sel.courseLocation } : {}),
      ...(sel.countryCode ? { countryCode: sel.countryCode } : {}),
      date: sel.date,
      timeOfDay: sel.timeOfDay ?? 'any',
      players: sel.players,
      holes: sel.holes,
      ...(sel.maxPricePerPatron != null ? { maxPricePerPatron: sel.maxPricePerPatron } : {}),
      phone: raw.phone.trim(),
      paymentType: raw.paymentType,
      ...(sel.place ? { place: sel.place } : {}),
      ...(sel.coordinates ? { coordinates: sel.coordinates } : {}),
    };

    this.submitted.set(true);
    this.action.emit({
      question: 'Confirm waitlist',
      action: payload,
    });
  }
}
